import type { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';

import { getAgentBasePathWithLocation, getBasePathWithLocation } from '@/router';
import type { DirectoryType } from '@/types/directoryType';

export const DIRECTORY_PERMANENT_REDIRECT = 'PERMANENT_REDIRECT';

type PermanentRedirectError = Error & {
  code: typeof DIRECTORY_PERMANENT_REDIRECT;
  redirectUri: string;
};

export const isDirectoryPermanentRedirectError = (error: unknown): boolean =>
  Boolean(
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as PermanentRedirectError).code === DIRECTORY_PERMANENT_REDIRECT,
  );

export const throwDirectoryPermanentRedirect = (redirectUri: string): never => {
  const error = new Error(DIRECTORY_PERMANENT_REDIRECT) as PermanentRedirectError;
  error.code = DIRECTORY_PERMANENT_REDIRECT;
  error.redirectUri = redirectUri;
  throw error;
};

export const buildDirectoryCanonicalRedirectPath = ({
  directoryType,
  locale,
  locationSlug,
  page,
}: {
  directoryType: DirectoryType;
  locale: HgSupportedLanguage;
  locationSlug: string;
  page: number;
}): string => {
  const path =
    directoryType === 'agent'
      ? getAgentBasePathWithLocation(locale, locationSlug)
      : getBasePathWithLocation(locale, locationSlug);

  const params = new URLSearchParams();

  if (page > 1) {
    params.set('page', String(page));
  }

  const search = params.toString();
  return search ? `${path}?${search}` : path;
};

/**
 * When the primary path segment resolves but is not the locale-canonical slug, throw 301.
 * National (no primary slug) and already-canonical slugs are left alone.
 */
export const assertCanonicalDirectoryLocationSlug = ({
  requestedPrimarySlug,
  resolvedLocations,
  locale,
  directoryType,
  page,
}: {
  requestedPrimarySlug: string | undefined;
  resolvedLocations: GeoLocation[];
  locale: HgSupportedLanguage;
  directoryType: DirectoryType;
  page: number;
}): void => {
  if (!requestedPrimarySlug || resolvedLocations.length === 0) {
    return;
  }

  const canonicalSlug = resolvedLocations[0].urlNames?.[locale];
  if (!canonicalSlug || requestedPrimarySlug === canonicalSlug) {
    return;
  }

  throwDirectoryPermanentRedirect(
    buildDirectoryCanonicalRedirectPath({
      directoryType,
      locale,
      locationSlug: canonicalSlug,
      page,
    }),
  );
};
